import API from './index';
import AppUrls from '../config/AppUrls';
import { buildPathWithParams } from '../utils/Paths';

class AccountsService {
  static fetchAccounts(filters) {
    return API.get(AppUrls.api.accounts, filters).then((response) => response.data);
  }

  static fetchAccountDetails(idUser) {
    const url = buildPathWithParams(AppUrls.api.accountDetails, { idUser });
    return API.get(url).then((response) => response.data);
  }

  static addAccountPhoto(idUser, file) {
    const url = buildPathWithParams(AppUrls.api.accountAddPhoto, { idUser });
    return API.post(url, { file }).then((response) => response.data);
  }

  static updateProfile(idUser, data) {
    const url = buildPathWithParams(AppUrls.api.accountEdit, { idUser });
    return API.post(url, data).then((response) => response.data);
  }

  static changePassword(idUser, passwordData) {
    const url = buildPathWithParams(AppUrls.api.changePassword, { idUser });
    return API.post(url, passwordData).then((response) => response.data);
  }

  static getMfaImage(idUser) {
    const url = buildPathWithParams(AppUrls.api.changeMfa, { idUser });
    return API.post(url, { mfaEnabled: true }).then((response) => response.data.qrUrl);
  }

  static enableMfa(idUser, mfaCode) {
    const url = buildPathWithParams(AppUrls.api.changeMfa, { idUser });
    return API.post(url, { mfaEnabled: true, mfaCode }).then((response) => response.data);
  }

  static disableMfa(idUser, mfaCode) {
    const url = buildPathWithParams(AppUrls.api.changeMfa, { idUser });
    return API.post(url, { mfaEnabled: false, mfaCode }).then((response) => response.data);
  }

  static fetchMerchants() {
    return API.get(AppUrls.api.merchants).then((response) => response.data);
  }

  static fetchRoles() {
    return API.get(AppUrls.api.roles).then((response) => response.data);
  }

  static fetchCapabilities(idMerchant) {
    const url = buildPathWithParams(AppUrls.api.capabilities, { idMerchant });
    return API.get(url).then((response) => response.data);
  }

  static fetchPresets() {
    return API.get(AppUrls.api.presets).then((response) => response.data);
  }

  static savePreset(name, data) {
    return API.post(AppUrls.api.savePreset, { name, capabilities: data }).then((response) => response.data);
  }

  static removePreset(idPreset) {
    const url = buildPathWithParams(AppUrls.api.removePreset, { idPreset });
    return API.delete(url).then((response) => {
      return response.data;
    });
  }

  static removeMultiplePreset(idPresets) {
    return API.delete(AppUrls.api.removePreset, idPresets).then((response) => {
      return response.data;
    });
  }

  static createAccount(data) {
    return API.post(AppUrls.api.createAccount, { ...data }).then((response) => {
      return response.data;
    });
  }

  static fetchMerchantAccount(idUser, idMerchant) {
    const url = buildPathWithParams(AppUrls.api.detailsAccountMerchant, { idUser, idMerchant });
    return API.get(url).then((response) => {
      return response.data;
    });
  }

  static fetchAstropayAccount(idUser) {
    const url = buildPathWithParams(AppUrls.api.detailsAccountAstro, { idUser });
    return API.get(url).then((response) => {
      return response.data;
    });
  }

  static editMerchantAccount(idUser, idMerchant, data) {
    const url = buildPathWithParams(AppUrls.api.editAccountMerchant, { idUser, idMerchant });
    return API.post(url, { ...data }).then((response) => {
      return response.data;
    });
  }

  static editAstropayAccount(idUser, data) {
    const url = buildPathWithParams(AppUrls.api.editAccountAstro, { idUser });
    return API.post(url, { ...data }).then((response) => {
      return response.data;
    });
  }

  static resetPassword(idUser) {
    const url = buildPathWithParams(AppUrls.api.resetAccountPass, { idUser });
    return API.post(url).then((response) => {
      return response.data;
    });
  }

  static resetMFA(idUser) {
    const url = buildPathWithParams(AppUrls.api.resetAccountMFA, { idUser });
    return API.post(url).then((response) => {
      return response.data;
    });
  }

  static resendActivationLink(idUser) {
    const url = buildPathWithParams(AppUrls.api.resendAccountActivation, { idUser });
    return API.post(url).then((response) => {
      return response.data;
    });
  }

  static enableMerchantAccount(idUser, idMerchant) {
    const url = buildPathWithParams(AppUrls.api.enableMerchantAccount, { idUser, idMerchant });
    return API.post(url).then((response) => {
      return response.data;
    });
  }

  static disableMerchantAccount(idUser, idMerchant) {
    const url = buildPathWithParams(AppUrls.api.disableMerchantAccount, { idUser, idMerchant });
    return API.post(url).then((response) => {
      return response.data;
    });
  }

  static enableAstroAccount(idUser) {
    const url = buildPathWithParams(AppUrls.api.enableAstroAccount, { idUser });
    return API.post(url).then((response) => {
      return response.data;
    });
  }

  static disableAstroAccount(idUser) {
    const url = buildPathWithParams(AppUrls.api.disableAstroAccount, { idUser });
    return API.post(url).then((response) => {
      return response.data;
    });
  }

  static removeAstroAccount(idUser) {
    const url = buildPathWithParams(AppUrls.api.removeAstroAccount, { idUser });
    return API.delete(url).then((response) => {
      return response.data;
    });
  }

  static restoreMerchantAccount(idUser, idMerchant) {
    const url = buildPathWithParams(AppUrls.api.restoreMerchantAccount, { idUser, idMerchant });
    return API.post(url).then((response) => {
      return response.data;
    });
  }

  static removeMerchantAccount(idUser, idMerchant) {
    const url = buildPathWithParams(AppUrls.api.removeMerchantAccount, { idUser, idMerchant });
    return API.delete(url).then((response) => {
      return response.data;
    });
  }

  static addMultiMerchant(idUser, idMerchant, values) {
    const url = buildPathWithParams(AppUrls.api.addMultiMerchant, { idUser, idMerchant });
    return API.post(url, { ...values }).then((response) => {
      return response.data;
    });
  }

  static fetchMerchantMailGroups(idMerchant) {
    const url = buildPathWithParams(AppUrls.api.merchantMailGroupList, { idMerchant });
    return API.get(url).then((response) => {
      return response.data;
    });
  }
}

export default AccountsService;
